import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { logEvent } from 'firebase/analytics';
import {
  CreateSurveyDto,
  SurveyFieldDto,
  CreateSurveyDtoPriorityEnum,
  SurveyRecipientDto,
  SurveyRecipientDtoStateEnum,
} from 'apps/marsinet/src/app/typescript-axios';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { configureAxios } from 'apps/marsinet/src/app/utils/utils';
import { formatUtcTimestamp } from 'apps/marsinet/src/app/utils/timezone-utils';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { ProgressBarComponent } from 'apps/marsinet/src/app/components/progress-bar/progress-bar';
import { TableRecipients } from './components/table-recipients';

const SurveyComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { survey_id } = useParams<{ survey_id: string }>();
  const { access_token, setError, selectedTimezone } = useContext(MarsinetContext);
  const { showToast: show } = useToast();

  const [survey, setSurvey] = useState<CreateSurveyDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<SurveyRecipientDto[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState<boolean>(false);

  const getAnonymizationTypeKey = (value: string): string => {
    const keyMap: Record<string, string> = {
      None: 'none',
      AllAnswers: 'all_answers',
      Optional: 'optional',
    };
    return keyMap[value] || value;
  };

  const getSendStrategyKey = (value: string): string => {
    const keyMap: Record<string, string> = {
      AllUsers: 'all_users',
      AdminOnly: 'admin_only',
    };
    return keyMap[value] || value;
  };

  const getFieldTypeKey = (value: string): string => {
    const keyMap: Record<string, string> = {
      text: 'text',
      tumber: 'number',
      boolean: 'boolean',
      select: 'select',
      multi_select: 'multiselect',
    };
    return keyMap[value] || value;
  };

  const timePausedByDefault = '9999-12-31T23:59:59.999Z'; // "pausado"

  useEffect(() => {
    const fetchSurveyData = async () => {
      if (!access_token || !survey_id) return;

      setLoading(true);
      configureAxios(access_token);

      try {
        const response =
          await apiServices.surveyApi.surveyControllerGetSurveyDetails(
            survey_id
          );
        setSurvey(response.data);
      } catch (error) {
        setError(true);
        show('error', t('messages.error'), t('messages.error_message'));
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchSurveyRecipients = async () => {
      if (!access_token || !survey_id) return;

      setLoadingRecipients(true);
      try {
        const response =
          await apiServices.surveyApi.surveyControllerGetSurveyRecipients(
            survey_id
          );
        setRecipients(response.data || []);
      } catch (error) {
        console.error('Error loading survey recipients', error);
        setRecipients([]);
      } finally {
        setLoadingRecipients(false);
      }
    };

    fetchSurveyData();
    fetchSurveyRecipients();
  }, [access_token, survey_id, setError, show, t]);

  if (loading) {
    return (
      <div className="surface-card border-round-2xl col-12 p-3 mt-4">
        <ProgressBarComponent />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="col-12 xl:pl-7 xl:pr-7">
        <div className="col-12 shadow-3 md:p-3 surface-card border-round-xl">
          <p className="text-center">{t('noData')}</p>
          <div className="flex justify-content-center mt-3">
            <Button
              label={t('goBack')}
              icon="pi pi-arrow-left"
              onClick={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      <div className="col-12 shadow-3 md:p-3 surface-card border-round-xl lg:h-full">
        {/* Header */}
        <div className="p-3 flex align-items-center">
          <Button
            icon="pi pi-angle-left"
            rounded
            onClick={() => navigate(-1)}
            severity="secondary"
            className="mr-4"
            aria-label="Go back"
          />
          <div className="flex align-items-baseline justify-content-end">
            <div className="mr-2">
              <i className="pi pi-list text-2xl text-900"></i>
            </div>
            <div className="font-medium text-2xl text-700">{survey.name}</div>
          </div>
        </div>

        {/* General Information Section */}
        <div className="col-12 mb-4">
          <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
            <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
            <div className="px-2 z-1 surface-50 flex align-items-center">
              <i className="pi pi-info-circle text-900 mr-2"></i>
              <span className="text-900 font-bold">
                {t('pages.surveys.information')}
              </span>
            </div>
          </div>
        </div>

        <div className="md:mx-8 pl-4 py-4">
          <div className="grid formgrid p-fluid">
            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.name')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {survey.name}
                </span>
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.priority')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  <Tag
                    value={t(
                      `pages.surveys.priority_values.${survey.priority}`
                    )}
                    severity={
                      survey.priority === CreateSurveyDtoPriorityEnum.Mandatory
                        ? 'danger'
                        : 'info'
                    }
                  />
                </span>
              </label>
            </div>

            <div className="col-12 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.description')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {survey.description || t('noData')}
                </span>
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.study')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {survey.study || t('noData')}
                </span>
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.anonymization_type')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {t(
                    `pages.surveys.anonymization_type_values.${getAnonymizationTypeKey(survey.anonymization_type)}`
                  )}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="col-12 mb-4">
          <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
            <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
            <div className="px-2 z-1 surface-50 flex align-items-center">
              <i className="pi pi-cog text-900 mr-2"></i>
              <span className="text-900 font-bold">
                {t('pages.surveys.configuration')}
              </span>
            </div>
          </div>
        </div>

        <div className="md:mx-8 pl-4 py-4">
          <div className="grid formgrid p-fluid">
            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.send_frequency_days')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {survey.send_frequency_days
                    ? `${survey.send_frequency_days} ${t('days')}`
                    : t('pages.surveys.one_time_send')}
                </span>
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.response_time_limit_days')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {survey.response_time_limit_days
                    ? `${survey.response_time_limit_days} ${t('days')}`
                    : t('pages.surveys.no_limit')}
                </span>
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.starts_at')}:{' '}
                {survey.starts_at === timePausedByDefault ? (
                  <span className="text-lg font-normal text-gray-800">
                    {t('pages.surveys.paused')}
                  </span>
                ) : (
                  <span className="text-lg font-normal text-gray-800">
                    {survey.starts_at
                      // La fecha programada es UTC; solo se adapta al mostrarla.
                      ? formatUtcTimestamp(
                          survey.starts_at,
                          selectedTimezone,
                          i18n.language,
                          { dateStyle: 'medium' }
                        )
                      : t('noData')}
                  </span>
                )}
              </label>
            </div>

            <div className="col-12 md:col-6 field mb-2">
              <label className="font-bold text-gray-800 block">
                {t('pages.surveys.send_strategy')}:{' '}
                <span className="text-lg font-normal text-gray-800">
                  {t(
                    `pages.surveys.send_strategy_values.${getSendStrategyKey(survey.send_strategy)}`
                  )}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Survey Fields Section */}
        {survey.template && survey.template.length > 0 && (
          <>
            <div className="col-12 mb-4">
              <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                <div className="px-2 z-1 surface-50 flex align-items-center">
                  <i className="pi pi-list text-900 mr-2"></i>
                  <span className="text-900 font-bold">
                    {t('pages.surveys.survey_fields')}
                  </span>
                </div>
              </div>
            </div>

            <div className="md:mx-8 pl-4 py-4">
              {survey.template.map((field: SurveyFieldDto, index: number) => (
                <div
                  key={index}
                  className="mb-4 pb-3"
                  style={{ borderBottom: '1px solid var(--surface-border)' }}
                >
                  <div className="grid formgrid p-fluid">
                    <div className="col-12 md:col-6 field mb-2">
                      <label className="font-bold text-gray-800 block">
                        {t('pages.surveys.field_title')}:{' '}
                        <span className="text-lg font-normal text-gray-800">
                          {field.name}
                        </span>
                      </label>
                    </div>

                    <div className="col-12 md:col-6 field mb-2">
                      <label className="font-bold text-gray-800 block">
                        {t('pages.surveys.field_type')}:{' '}
                        <span className="text-lg font-normal text-gray-800">
                          {t(
                            `pages.surveys.field_types.${getFieldTypeKey(field.type)}`
                          )}
                        </span>
                      </label>
                    </div>

                    <div className="col-12 field mb-2">
                      <label className="font-bold text-gray-800 block">
                        {t('pages.surveys.required')}:{' '}
                        <Tag
                          value={field.required ? t('yes') : t('no')}
                          severity={field.required ? 'success' : 'warning'}
                        />
                      </label>
                    </div>

                    {field.allowed_values &&
                      field.allowed_values.length > 0 && (
                        <div className="col-12 field mb-2">
                          <label className="font-bold text-gray-800 block">
                            {t('pages.surveys.added_options')}:{' '}
                          </label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {field.allowed_values.map(
                              (value: string, idx: number) => (
                                <Tag key={idx} value={value} />
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {field.condition && (
                      <div className="col-12 field mb-2">
                        <label className="font-bold text-gray-800 block">
                          📌 {t('pages.surveys.field_dependency')}:{' '}
                          <span className="text-lg font-normal text-gray-800">
                            {field.condition.field_name}: {field.condition.value}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Recipients Section */}
        {recipients.length > 0 && (
          <>
            <div className="col-12 mb-4">
              <div className="flex w-full relative align-items-center justify-content-center my-3 px-4">
                <div className="border-top-1 surface-border top-50 left-0 absolute w-full"></div>
                <div className="px-2 z-1 surface-50 flex align-items-center">
                  <i className="pi pi-users text-900 mr-2"></i>
                  <span className="text-900 font-bold">
                    {t('pages.surveys.recipients')}
                  </span>
                </div>
              </div>
            </div>

            <div className="md:mx-8 pl-4 py-4">
              <div className="mb-3 p-3 surface-ground border-round-md flex justify-content-between">
                <span className="font-bold text-900">
                  {t('pages.surveys.responses')}:{' '}
                  <span className="text-lg font-bold text-primary">
                    {
                      recipients.filter(
                        (r) => r.state === SurveyRecipientDtoStateEnum.Answered
                      ).length
                    }
                    /{recipients.length}
                  </span>
                </span>
                <span className="font-bold text-900">
                  {t('pages.surveys.overdue_users')}:{' '}
                  <span className="text-lg font-bold text-primary">
                    {
                      recipients.filter(
                        (r) => r.state === SurveyRecipientDtoStateEnum.Overdue
                      ).length
                    }
                  </span>
                </span>
              </div>

              <TableRecipients
                recipients={recipients}
                loadingRecipients={loadingRecipients}
                t={t}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyComponent;
